import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { ButtonT1 } from "../../../../../components/atoms/Button";
import { formatColumnLabel } from "../../../../../helper/columnLabel";
import { useNotification } from "../../../../../hooks/useNotification";
import {
  useCreateViewMutation,
  useUpdateViewMutation,
} from "../../../../../store/apiSlice/newInterfaceApiSlice";
import { useLazyGetPurchaseOrderStatusV2Query } from "../../../../../store/apiSlice/purchaseOrderApiSlice";
import { useGetConsignmentStatusQuery } from "../../../../../store/apiSlice/shipmentApiSlice";
import { setCurrentViewInLocals } from "../../../../../store/slice/newInterfaceSlice";
import { ReactComponent as DragMenu } from "../../../assets/svgIcon/listView/dragmenu.svg";
import { allocationDataByType } from "../../Allocation/SelectAllocation";
import { moduleForView, orderTypeForUrl, selectState } from "../../utils/tableDataElements";
import { BOARD_CARD_HERO } from "../Board/boardCardDisplay";
import { CreateViewDrawer } from "./CreateViewDrawer";

/**
 * CreateView Component
 *
 * UI for creating or editing saved views (both list and board views) in modules like shipment,
 * transfer, purchase, and inventory. Supports personal or organizational views, custom column
 * selection (with drag-and-drop ordering), and sub-type selection like status or milestone.
 *
 * @component
 *
 * @param {Function} closeCreateView - Callback to close the create/edit view drawer.
 * @param {boolean} boardView - If true, configures the component for board view creation.
 * @param {boolean} editView - If true, puts the component in edit mode.
 * @param {Object} selectedCurrentView - The currently selected view object to be edited.
 * @param {string} orderType - The order type/module (e.g., "shipment", "purchase", "inventory").
 * @param {Function} setSelectedCurrentView - Updates the current view state in the parent.
 * @param {string} [allocation] - Allocation type, only used if `orderType` is "allocation".
 *
 */

export function CreateView({
  closeCreateView,
  boardView,
  editView,
  selectedCurrentView,
  orderType: orderTypeForCreate,
  setSelectedCurrentView,
  allocation,
}) {
  const notify = useNotification();
  const [dropDownValue, setdropDownValue] = useState("status");
  const [createViewForCommon, { isLoading: createViewLoading }] = useCreateViewMutation();
  const [updateView, { isLoading: updateViewLoading }] = useUpdateViewMutation();
  const isSavingView = useRef(false);
  const dispatch = useDispatch();
  useGetConsignmentStatusQuery(undefined, { skip: orderTypeForCreate !== "shipment" });
  const [fetchPurchaseOrderStatus] = useLazyGetPurchaseOrderStatusV2Query();

  useEffect(() => {
    if (orderTypeForCreate === "purchase") {
      fetchPurchaseOrderStatus();
    }
  }, [fetchPurchaseOrderStatus, orderTypeForCreate]);

  const [selectedViewItem, setSelectedViewItem] = useState([]);
  const [viewName, setViewName] = useState("");
  const {
    listView: listFromServer,
    boardView: boardViewFromServer,
    completeListOfViews,
  } = useSelector(state => {
    if (orderTypeForCreate === "shipment") {
      return state.newInterface.allViews;
    } else if (orderTypeForCreate === "transfer") {
      return state.newTransferOrder.allViews;
    } else if (orderTypeForCreate === "purchase") {
      return state.newPurchaseOrder.allViews;
    } else if (orderTypeForCreate === "allocation") {
      return state.newTransferOrder.allocation[allocation];
    } else if (
      orderTypeForCreate === "inventory" ||
      orderTypeForCreate === "inventoryDetail" ||
      orderTypeForCreate === "inventoryTransactions"
    ) {
      return state.newWarehouse.inventory[orderTypeForCreate];
    } else if (orderTypeForCreate === "receipt") {
      return state.receipt.receiptsLayout;
    } else if (orderTypeForCreate === "product") {
      return state.product.productsLayout;
    } else if (orderTypeForCreate === "productCategory") {
      return state.product.productCategoryLayout;
    } else if (orderTypeForCreate === "productTag") {
      return state.product.productTagLayout;
    } else if (orderTypeForCreate === "productAlias") {
      return state.product.productAliasLayout;
    } else if (orderTypeForCreate === "productPlan") {
      return state.product.productPlanLayout;
    } else if (orderTypeForCreate === "pack") {
      return state.product.packLayout;
    } else if (orderTypeForCreate === "stockPack") {
      return state.newWarehouse.stockPackLayout;
    } else if (orderTypeForCreate === "warehouse") {
      return state.supplier.warehouseLayout;
    } else if (orderTypeForCreate === "warehouseCostingRange") {
      return state.newWarehouse.warehouseCostingRangeLayout;
    } else if (orderTypeForCreate === "stockZone") {
      return state.newWarehouse.stockZoneLayout;
    } else if (orderTypeForCreate === "stockLocation") {
      return state.newWarehouse.stockLocationLayout;
    } else if (orderTypeForCreate === "standardCost") {
      return state.newWarehouse.standardCostLayout;
    } else if (orderTypeForCreate === "forecast") {
      return state.newWarehouse.forecastLayout;
    } else if (orderTypeForCreate === "supplier") {
      return state.supplier.supplierLayout;
    } else if (orderTypeForCreate === "warehouseCosting") {
      return state.newWarehouse.warehouseCostingLayout;
    } else if (orderTypeForCreate === "transactionReason") {
      return state.report.transactionReasonLayout;
    } else if (orderTypeForCreate === "chartsOfAccount") {
      return state.account.chartsAccountLayout;
    } else if (orderTypeForCreate === "paymentTerm") {
      return state.account.paymentTermsLayout;
    } else if (orderTypeForCreate === "journalLine") {
      return state.account.journalLineLayout;
    } else if (orderTypeForCreate === "taxRate") {
      return state.tax.taxRateLayout;
    } else if (orderTypeForCreate === "site") {
      return state.newWarehouse.siteLayout;
    } else if (orderTypeForCreate === "workCenter") {
      return state.newWarehouse.workCenterLayout;
    } else if (orderTypeForCreate === "people") {
      return state.production.peopleLayout;
    } else if (orderTypeForCreate === "users") {
      return state.user.usersLayout;
    } else if (orderTypeForCreate === "boms") {
      return state.production.bomsLayout;
    } else if (orderTypeForCreate === "manufacturingOrder") {
      return state.production.manufacturingOrderLayout;
    } else if (orderTypeForCreate === "bill") {
      return state.invoice.billLayout;
    } else if (orderTypeForCreate === "saleInvoice") {
      return state.invoice.saleInvoiceLayout;
    } else if (orderTypeForCreate === "receiptAdjustment") {
      return state.receipt.receiptAdjustmentLayout;
    } else if (orderTypeForCreate === "syncHistory") {
      return state.integration.syncHistoryLayout;
    } else if (orderTypeForCreate === "prePayment") {
      return state.invoice.prePaymentLayout;
    } else if (orderTypeForCreate === "supplierCreditNote") {
      return state.invoice.supplierCreditNoteLayout;
    } else if (orderTypeForCreate === "customerCreditNote") {
      return state.invoice.customerCreditNoteLayout;
    } else if (orderTypeForCreate === "stockReplenishment") {
      return state.newWarehouse.stockReplenishmentLayout;
    } else if (orderTypeForCreate === "plannedOrder") {
      return state.planner.plannedOrdersLayout;
    } else if (orderTypeForCreate === "picklist") {
      return state.newWarehouse.picklistLayout;
    } else if (orderTypeForCreate === "customer") {
      return state.customer.customerLayout;
    } else if (orderTypeForCreate === "taxCategory") {
      return state.tax.taxCategoryLayout;
    } else if (orderTypeForCreate === "customerTag") {
      return state.customer.customerTagLayout;
    } else if (orderTypeForCreate === "customerSegment") {
      return state.customer.customerSegmentLayout;
    } else if (orderTypeForCreate === "unitOfMeasure") {
      return state.unitOfMeasure.unitOfMeasureLayout;
    } else if (orderTypeForCreate === "equipmentType") {
      return state.shipment.equipmentTypeLayout;
    } else if (orderTypeForCreate === "equipment") {
      return state.shipment.equipmentLayout;
    } else if (orderTypeForCreate === "productTaxClassification") {
      return state.tax.productTaxClassificationLayout;
    } else if (orderTypeForCreate === "salesRep") {
      return state.sales.salesRepLayout;
    } else if (orderTypeForCreate === "pricelist") {
      return state.pricing.pricelistLayout;
    } else if (orderTypeForCreate === "promo") {
      return state.promo.promoLayout;
    } else if (orderTypeForCreate === "quote") {
      return state.sales.quoteLayout;
    } else if (orderTypeForCreate === "salesOrder") {
      return state.sales.salesOrderLayout;
    } else if (orderTypeForCreate === "delivery") {
      return state.shipment.deliveryLayout;
    } else if (orderTypeForCreate === "shipmentV1") {
      return state.shipment.shipmentsV1Layout;
    } else if (orderTypeForCreate === "asn") {
      return state.shipment.asnLayout;
    } else if (orderTypeForCreate === "shipmentLeg") {
      return state.shipment.shipmentLegsLayout;
    } else if (orderTypeForCreate === "customerReturn") {
      return state.sales.customerReturnLayout;
    } else if (orderTypeForCreate === "shopifyCatalogue") {
      return state.integration.shopifyCatalogueLayout;
    } else if (orderTypeForCreate === "shopifyStores") {
      return state.integration.shopifyStoresLayout;
    } else if (orderTypeForCreate === "coManufacturingOrder") {
      return state.manufacturingOrder.coManufacturingOrderLayout;
    } else if (orderTypeForCreate === "billChargeType") {
      return state.invoice.billChargeTypeLayout;
    }
    return {
      listView: { allItemsName: [] },
      boardView: { status: { allItemsName: [] } },
      completeListOfViews: [],
    };
  });

  const { milestoneList } = useSelector(state => state[selectState[orderTypeForCreate]] || {});
  const { consignmentStatus } = useSelector(state => state.shipment);
  const { purchaseOrderStatusV2 } = useSelector(state => state.purchaseOrder);
  const [isPersonal, setIsPersonal] = useState(false);
  const [boardCardHero, setBoardCardHero] = useState(BOARD_CARD_HERO.NUMBER);

  const supportsBoardCardSettings =
    boardView && (orderTypeForCreate === "shipment" || orderTypeForCreate === "purchase");

  const buildViewMeta = () => {
    if (!supportsBoardCardSettings) {
      return undefined;
    }
    const existing =
      completeListOfViews?.find(item => item.id === selectedCurrentView?.id)?.meta || {};
    return {
      ...existing,
      board_card: { hero: boardCardHero },
    };
  };

  const combinedAvailableItems = useMemo(() => {
    if (
      dropDownValue !== "combined" ||
      (orderTypeForCreate !== "shipment" && orderTypeForCreate !== "purchase")
    ) {
      return [];
    }
    const statusSource =
      orderTypeForCreate === "purchase" ? purchaseOrderStatusV2 || [] : consignmentStatus;
    const statuses = statusSource.map(status => ({
      columnName: status.name || status,
      checked: true,
      kind: "status",
      key: status.name || status,
    }));
    const milestones = milestoneList.map(item => ({
      columnName: item.name,
      checked: true,
      kind: "milestone",
      id: item.id,
    }));
    return [...statuses, ...milestones];
  }, [consignmentStatus, dropDownValue, milestoneList, orderTypeForCreate, purchaseOrderStatusV2]);

  const displayColumnName = name => (boardView ? name : formatColumnLabel(name));

  const columnNames = useMemo(() => {
    if (!boardView) return listFromServer.allItemsName;

    if (dropDownValue === "combined") {
      return combinedAvailableItems.map(item => item.columnName);
    }

    if (dropDownValue === "milestone") {
      return milestoneList.map(item => item.name);
    }

    if (orderTypeForCreate === "shipment" && consignmentStatus.length > 0) {
      return consignmentStatus.map(status => status.name);
    }

    if (orderTypeForCreate === "purchase" && purchaseOrderStatusV2?.length > 0) {
      return purchaseOrderStatusV2.map(status => status.name || status);
    }

    return boardViewFromServer?.status.allItemsName || [];
  }, [
    boardView,
    dropDownValue,
    milestoneList,
    consignmentStatus,
    orderTypeForCreate,
    purchaseOrderStatusV2,
    boardViewFromServer?.status.allItemsName,
    listFromServer.allItemsName,
    combinedAvailableItems,
  ]);

  const combinedItemKey = item =>
    item.kind === "milestone" ? `milestone-${item.id}` : `status-${item.key || item.columnName}`;

  const handleSelectViewOption = e => {
    if (dropDownValue === "combined") {
      if (e.target.value === "selectAll") {
        if (e.target.checked) {
          setSelectedViewItem(combinedAvailableItems.map(item => ({ ...item, checked: true })));
        } else {
          setSelectedViewItem([]);
        }
        return;
      }
      const entryKey = e.target.getAttribute("data-entry-key");
      const existing = selectedViewItem.find(item => combinedItemKey(item) === entryKey);
      if (e.target.checked) {
        if (existing) {
          return;
        }
        const source = combinedAvailableItems.find(item => combinedItemKey(item) === entryKey);
        if (source) {
          setSelectedViewItem([...selectedViewItem, { ...source, checked: true }]);
        }
      } else {
        setSelectedViewItem(selectedViewItem.filter(item => combinedItemKey(item) !== entryKey));
      }
      return;
    }

    if (e.target.value === "selectAll") {
      if (e.target.checked) {
        setSelectedViewItem(
          columnNames.map((item, ind) => ({
            columnName: item,
            checked: true,
            id: dropDownValue === "milestone" ? milestoneList[ind]?.id : null,
          })),
        );
      } else {
        setSelectedViewItem([]);
      }
      return;
    }
    if (e.target.checked) {
      setSelectedViewItem([
        ...selectedViewItem,
        {
          columnName: e.target.value,
          checked: true,
          id: dropDownValue === "milestone" ? e.target.getAttribute("data-id") : null,
        },
      ]);
    } else {
      setSelectedViewItem(selectedViewItem.filter(item => item.columnName !== e.target.value));
    }
  };

  const handleSelectRightSide = e => {
    if (!e.target.checked) {
      setSelectedViewItem(selectedViewItem.filter(item => item.columnName !== e.target.value));
    }
  };

  const onlyCheckedSelectedColumnName = () =>
    selectedViewItem.reduce((acc, item) => {
      if (item.checked === false) {
        return acc;
      }

      if (dropDownValue === "combined") {
        if (item.kind === "milestone") {
          acc.push({ kind: "milestone", id: Number(item.id) });
        } else {
          acc.push({ kind: "status", key: item.key || item.columnName });
        }
      } else if (dropDownValue === "milestone") {
        acc.push(item.id);
      } else {
        acc.push(item.columnName);
      }
      return acc;
    }, []);

  const handleCreateView = async e => {
    // RTK Query's loading state is applied on the next render. A ref blocks a
    // second click in that small window from creating a duplicate saved view.
    if (isSavingView.current) return;

    isSavingView.current = true;
    let selectedView = { ...selectedCurrentView };
    try {
      if (editView) {
        const updatePayload = {
          name: viewName,
          sequence: onlyCheckedSelectedColumnName(),
          is_personal: isPersonal,
        };
        const meta = buildViewMeta();
        if (meta) {
          updatePayload.meta = meta;
        }
        await updateView({
          id: selectedCurrentView.id,
          data: updatePayload,
        }).unwrap();
        selectedView = { ...selectedView, value: viewName };
        notify({
          type: "success",
          message: `View "${viewName}" successfully updated!`,
        });
      } else {
        let reqData = {
          is_personal: isPersonal,
          module: orderTypeForUrl[orderTypeForCreate]?.module,
          name: viewName,
          sequence: onlyCheckedSelectedColumnName(),
          type: boardView ? "board" : "list",
          sub_type: boardView ? dropDownValue : null,
        };
        const meta = buildViewMeta();
        if (meta) {
          reqData.meta = meta;
        }
        if (orderTypeForCreate === "allocation")
          reqData["module"] = allocationDataByType[allocation].moduleName;
        const res = await createViewForCommon(reqData).unwrap();

        selectedView = {
          value: res.name,
          sub_type: boardView ? dropDownValue : null,
          id: res.id,
          is_view_rule_exists: res.filter_criteria,
        };
        notify({
          type: "success",
          message: `View "${res.name}" successfully created!`,
        });
      }

      dispatch(
        setCurrentViewInLocals({
          view: selectedView,
          module: moduleForView[orderTypeForCreate],
          allocation,
        }),
      );
      setSelectedCurrentView(selectedView);
      closeCreateView(e, selectedViewItem);
    } catch (error) {
      notify({
        type: "error",
        message:
          error?.data?.detail ||
          error?.data?.message ||
          `Failed to ${editView ? "update" : "create"} view. Please try again.`,
      });
    } finally {
      isSavingView.current = false;
    }
  };

  const handleViewName = e => {
    setViewName(e.target.value);
  };

  const handlePersonalView = e => {
    if (e.target.value === "personal") {
      setIsPersonal(true);
    } else {
      setIsPersonal(false);
    }
  };

  // Drag and Drop- set view items in order
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("dragIndex", index);
  };

  const handleDrop = (e, dropIndex) => {
    const dragIndex = e.dataTransfer.getData("dragIndex");
    if (dragIndex !== null && dragIndex !== dropIndex) {
      const updatedItems = [...selectedViewItem];
      const draggedItem = updatedItems.splice(dragIndex, 1)[0];
      updatedItems.splice(dropIndex, 0, draggedItem);
      setSelectedViewItem(updatedItems);
    }
  };

  const handleDragOver = e => {
    e.preventDefault();
  };

  useEffect(() => {
    if (editView) {
      setSelectedViewItem(
        (boardView ? boardViewFromServer[selectedCurrentView.sub_type] : listFromServer)?.allViews[
          selectedCurrentView.value
        ].map(item => {
          if (selectedCurrentView.sub_type === "combined") {
            if (item?.kind === "milestone") {
              const milestone = milestoneList.find(
                milestoneItem => milestoneItem.id === Number(item.id),
              );
              return {
                columnName: milestone?.name || `Milestone ${item.id}`,
                checked: true,
                kind: "milestone",
                id: item.id,
              };
            }
            return {
              columnName: item.key,
              checked: true,
              kind: "status",
              key: item.key,
            };
          }
          if (selectedCurrentView.sub_type === "milestone") {
            let colomnName = item;
            let condition = milestoneList.find(milestone => milestone.id === Number(item));
            if (milestoneList.find(milestone => milestone.id === Number(item))) {
              colomnName = condition.name;
            }
            return { columnName: colomnName, checked: true, id: item };
          }
          return { columnName: item, checked: true };
        }),
      );

      const findView = completeListOfViews.find(item => item.id === selectedCurrentView.id);
      if (findView) {
        setIsPersonal(findView.is_personal);
        const hero = findView.meta?.board_card?.hero;
        setBoardCardHero(
          hero === BOARD_CARD_HERO.NAME ? BOARD_CARD_HERO.NAME : BOARD_CARD_HERO.NUMBER,
        );
      }

      setViewName(selectedCurrentView.value);
      setdropDownValue(selectedCurrentView.sub_type);
    }
    // eslint-disable-next-line
  }, []);

  const handleSelectForWarning = () => {};

  const handleDropDown = e => {
    setdropDownValue(e.target.value);
    setSelectedViewItem([]);
  };

  const numberHeroLabel = orderTypeForCreate === "shipment" ? "Shipment number" : "PO number";
  const nameHeroLabel = orderTypeForCreate === "shipment" ? "Shipment name" : "Order name";
  const drawerTitle = `${editView ? "Edit" : "Create"} View`;
  const boardCardToggle = supportsBoardCardSettings ? (
    <BoardCardHeroToggle>
      <span>Card title</span>
      <button
        type="button"
        className={boardCardHero === BOARD_CARD_HERO.NUMBER ? "selected" : ""}
        onClick={() => setBoardCardHero(BOARD_CARD_HERO.NUMBER)}
      >
        {numberHeroLabel}
      </button>
      <button
        type="button"
        className={boardCardHero === BOARD_CARD_HERO.NAME ? "selected" : ""}
        onClick={() => setBoardCardHero(BOARD_CARD_HERO.NAME)}
      >
        {nameHeroLabel}
      </button>
    </BoardCardHeroToggle>
  ) : null;

  return (
    <CreateViewDrawer
      isOpen
      title={drawerTitle}
      supplementary={boardCardToggle}
      onClose={closeCreateView}
    >
      <CreateViewCss className="inDrawer">
        <main>
          <div className="viewMetaRow">
            <label>
              <span>Name of the View </span>
              <input
                onChange={handleViewName}
                value={viewName}
                placeholder="Enter view name"
                type="text"
              />
            </label>
            <label>
              <span>Visibility</span>
              <select
                onChange={handlePersonalView}
                className="selectBox"
                value={isPersonal ? "personal" : "organizational"}
              >
                <option value="organizational">Organizational View</option>
                <option value="personal">Personal View</option>
              </select>
            </label>
            {!editView && boardView && (
              <label>
                <span>Board type</span>
                <select onChange={handleDropDown} className="selectBox" value={dropDownValue}>
                  <option value="status">Status View</option>
                  <option value="milestone">MileStone View</option>
                  {(orderTypeForCreate === "shipment" || orderTypeForCreate === "purchase") && (
                    <option value="combined">Combined View</option>
                  )}
                </select>
              </label>
            )}
          </div>

          <div className="detailsOfView">
            <div className="leftSide">
              <div onClick={handleSelectViewOption} className="availableColumn">
                <p>Available Columns</p>
                <div>
                  <label className="selectAll">
                    <input
                      type="checkbox"
                      value="selectAll"
                      checked={columnNames.length === selectedViewItem.length}
                    />
                    Select all
                  </label>
                  {dropDownValue === "combined"
                    ? combinedAvailableItems.map(item => (
                        <label key={combinedItemKey(item)}>
                          <input
                            type="checkbox"
                            onChange={handleSelectViewOption}
                            checked={selectedViewItem?.some(
                              selected => combinedItemKey(selected) === combinedItemKey(item),
                            )}
                            data-kind={item.kind}
                            data-entry-key={combinedItemKey(item)}
                            value={item.columnName}
                          />
                          {item.columnName}
                          <span className="columnKindTag">
                            {item.kind === "milestone" ? "Milestone" : "Status"}
                          </span>
                        </label>
                      ))
                    : columnNames.map((item, ind) => (
                        <label key={item}>
                          <input
                            type="checkbox"
                            onChange={handleSelectForWarning}
                            checked={selectedViewItem?.some(name => name.columnName === item)}
                            data-id={dropDownValue === "milestone" ? milestoneList[ind].id : null}
                            value={item}
                          />
                          {displayColumnName(item)}
                        </label>
                      ))}
                </div>
              </div>
            </div>

            <div className="rightSide">
              <p>Selected Columns</p>
              <div className="selectedColumns ">
                {selectedViewItem?.map((item, index) => (
                  <label
                    draggable
                    onDragStart={e => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={e => handleDrop(e, index)}
                    key={dropDownValue === "combined" ? combinedItemKey(item) : item.columnName}
                    className="item"
                  >
                    <DragMenu />
                    <input
                      type="checkbox"
                      checked={item.checked}
                      value={item.columnName}
                      onChange={handleSelectRightSide}
                    />
                    {displayColumnName(item.columnName)}
                    {dropDownValue === "combined" && (
                      <span className="columnKindTag">
                        {item.kind === "milestone" ? "Milestone" : "Status"}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </main>

        <footer>
          <ButtonT1 onClickCallback={closeCreateView} title={"Cancel"} $variant="primaryLight" />

          <ButtonT1
            title={`${editView ? "Update" : "Create"} View`}
            disabled={!(viewName && selectedViewItem?.length)}
            onClickCallback={handleCreateView}
            loading={createViewLoading || updateViewLoading}
          />
        </footer>
      </CreateViewCss>
    </CreateViewDrawer>
  );
}

export const CreateViewCss = styled.div`
  position: absolute;
  background-color: white;
  left: 0;
  right: 0;
  top: 1rem;
  z-index: 11;
  width: 35vw;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: Inter;
  margin: 5vh auto;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.05));
  border: 1px solid var(--PEARL, #ecf1f4);
  background-color: white;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  border-radius: 4px;

  .columnKindTag {
    margin-left: 8px;
    font-size: 11px;
    color: #7a7a94;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  &.inDrawer {
    position: relative;
    left: auto;
    right: auto;
    top: auto;
    z-index: auto;
    width: 100%;
    height: 100%;
    min-height: 0;
    margin: 0;
    filter: none;
    border: none;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    flex: 1;

    > main {
      flex: 1;
      min-height: 0;
      max-height: none;
      overflow: hidden;
      display: flex;
      flex-direction: column;

      > .viewMetaRow {
        display: flex;
        gap: 16px;
        flex-shrink: 0;
        margin: 16px 24px 12px;

        > label {
          flex: 1;
          margin: 0;
          min-width: 0;

          > input,
          > select {
            height: 40px;
            box-sizing: border-box;
          }
        }
      }

      > .detailsOfView {
        display: flex;
        flex: 1;
        min-height: 0;
        margin: 0 24px 16px;

        > .leftSide,
        > .rightSide {
          width: 50%;
          flex: 1 1 50%;
          min-width: 0;
          min-height: 0;
          height: auto;
          max-height: none;
          margin: 0;
          overflow-y: auto;
          box-sizing: border-box;
        }

        > .leftSide {
          padding-right: 16px;
          border-right: 1px solid #e9edf5;
        }

        > .rightSide {
          padding-left: 16px;

          > p {
            margin-left: 3px;
          }
        }
      }
    }

    > footer {
      flex-shrink: 0;
      margin-top: auto;
    }
  }

  &.editColumns {
    > main {
      margin-top: 12px;
    }

    > footer {
      height: auto;
      padding: 12px 16px;
      gap: 8px;
      border-top: 1px solid #ecf1f4;
      box-shadow: none;

      button {
        height: 34px;
        padding: 0 16px;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 600;
        line-height: 20px;
        cursor: pointer;
        transition:
          background-color 0.15s ease,
          border-color 0.15s ease,
          color 0.15s ease;
      }

      .cancelBtn {
        border: 1px solid #d5deea;
        background: #fff;
        color: #1249bf;
      }

      .cancelBtn:hover {
        background: #f4f7ff;
        border-color: #2060ea;
        color: #1249bf;
      }

      .applyBtn {
        border: 1px solid #2060ea;
        background: #2060ea;
        color: #fff;
      }

      .applyBtn:hover:not(:disabled) {
        background: #1249bf;
        border-color: #1249bf;
        color: #fff;
      }

      .applyBtn:disabled {
        background: #f7f8f9;
        border-color: #e5eaf0;
        color: rgba(0, 0, 0, 0.25);
        cursor: not-allowed;
      }
    }
  }

  > header {
    display: flex;
    justify-content: space-between;
    border-radius: 4px 4px 0px 0px;
    border-bottom: 1px solid #e9edf5;
    background: #f9fafd;
    padding: 16px 16px 8px;
    font-weight: 600;
    line-height: 24px;

    svg {
      cursor: pointer;
    }
  }
  > main {
    display: flex;
    flex-direction: column;
    max-height: 75vh;

    > label,
    .viewMetaRow > label {
      margin: 16px;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      > span {
        display: flex;
        flex-direction: column;
        color: var(--GREY, #4a4a68);
        height: 22px;
        font-feature-settings:
          "liga" off,
          "clig" off;
        line-height: 20px; /* 166.667% */
        margin-bottom: 4px;
      }

      > input {
        height: 24px;
        padding: 8px 4px 8px 12px;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 22px;
        border-radius: 4px;
        border: 1px solid var(--LIGHT-ASH, #b9b9c6);
        color: var(--DARK-GREY, #0e0e2c);
        outline: none;

        &:focus {
          border: 1px solid var(--DARK-BLUE, #1249bf);
        }

        &::placeholder {
          color: var(--LIGHT-ASH, #b9b9c6);
        }
      }
    }

    .selectBox {
      font-weight: 300;
      border-radius: 4px;
      border: 1px solid var(--LIGHT-ASH, #b9b9c6);
      width: 100%;
      height: 40px;
      padding: 0 8px;
      color: var(--DARK-GREY, #0e0e2c);
      background: #fff;
      outline: none;

      &:focus {
        border: 1px solid var(--DARK-BLUE, #1249bf);
      }
    }

    > .detailsOfView {
      display: flex;
      margin: 0 16px;

      .selectBox {
        font-weight: 300;
        border-radius: 4px;
        border: 1px solid var(--LIGHT-ASH, #b9b9c6);
        width: 100%;
      }
      > .leftSide {
        display: flex;
        flex-direction: column;
        width: 50%;
        font-weight: 400;
        border-right: 1px solid #e9edf5;
        gap: 16px;
        max-height: 65vh;
        overflow-y: auto;
        > .searchBox {
          display: flex;
          height: 24px;
          padding: 4px 4px 4px 12px;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          flex-shrink: 0;
          font-size: 12px;
          border-radius: 4px;
          border: 1px solid var(--Light---ui-border-color, #c5c7d0);
          background-color: #fff;

          > input {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            height: 22px;
            border: none;
            outline: none;

            &::placeholder {
              color: var(--LIGHT-ASH, #b9b9c6);
            }
          }
          svg {
            width: 24px;
            cursor: pointer;
          }
        }
        > .availableColumn {
          display: flex;
          flex-direction: column;

          > p {
            color: var(--GREY, #4a4a68);
            font-size: 10px;
            margin-left: 3px;
            margin-bottom: 0;
          }
          .selectAll {
            font-weight: 500;
          }

          div > label {
            display: flex;
            padding: 5px 12px;
            align-items: center;
            gap: 8px;
            cursor: pointer;

            > input {
              border-radius: 2px;
              border: 1px solid var(--LIGHT-GREY, #8c8ca1);
            }
          }
        }
      }

      > .rightSide {
        width: 50%;
        margin: 0 16px;
        height: 65vh;
        overflow-y: auto;

        > p {
          color: var(--GREY, #4a4a68);
          font-size: 10px;
          margin-left: 12px;
          margin-bottom: 0;
        }
        > .selectedColumns {
          > .item {
            display: flex;
            padding: 5px 12px;
            align-items: center;
            gap: 8px;
            cursor: grab;
          }
        }
      }
    }
  }

  > footer {
    height: 48px;
    display: flex;
    justify-content: end;
    padding: 8px 16px;
    gap: 16px;
    border-radius: 4px 4px 0px 0px;
    background-color: #fff;
    box-shadow: 0px -2px 4px 0px rgba(0, 0, 0, 0.1);
  }
`;

const BoardCardHeroToggle = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  margin-right: 8px;

  > span {
    color: var(--GREY, #4a4a68);
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }

  > button {
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--LIGHT-ASH, #b9b9c6);
    background: #fff;
    color: var(--DARK-GREY, #0e0e2c);
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 30px;
    cursor: pointer;
    white-space: nowrap;

    &:first-of-type {
      border-radius: 4px 0 0 4px;
    }

    &:last-of-type {
      border-radius: 0 4px 4px 0;
      margin-left: -1px;
    }

    &:hover {
      border-color: var(--DARK-BLUE, #1249bf);
      z-index: 1;
    }

    &.selected {
      border-color: var(--DARK-BLUE, #1249bf);
      background: #f0f5ff;
      color: var(--DARK-BLUE, #1249bf);
      z-index: 1;
    }
  }
`;
